import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Toast, toastError } from '@/plugins/toast.js'
import { userCompaniesApi } from '@/services/api'
import { useAuthStore } from './auth'

export const useCompanyContextStore = defineStore('companyContext', () => {
  const authStore = useAuthStore()

  const companies = ref([])
  const currentCompany = ref(null)
  const isLoading = ref(false)

  const hasCompanies = computed(() => companies.value.length > 0)
  const currentCompanyId = computed(() => currentCompany.value?.id || null)

  const fetchUserCompanies = async () => {
    isLoading.value = true
    try {
      const response = await userCompaniesApi.getUserCompanies()
      companies.value = response.data.companies || []

      if (companies.value.length > 0) {
        if (response.data.defaultCompanyId) {
          const defaultCompany = companies.value.find(c => c.id === response.data.defaultCompanyId)
          if (defaultCompany) {
            currentCompany.value = defaultCompany
          } else {
            currentCompany.value = companies.value[0]
          }
        } else {
          currentCompany.value = companies.value[0]
        }

        saveCurrentCompanyToStorage()
      } else {
        currentCompany.value = null
        localStorage.removeItem('currentCompany')
      }

      return response
    } catch (error) {
      companies.value = []
      currentCompany.value = null
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const setCurrentCompany = async (companyId) => {
    try {
      const company = companies.value.find(c => c.id === companyId)
      if (!company) {
        Toast.fire({
          icon: 'error',
          title: 'Empresa no encontrada'
        });
        return false
      }

      await userCompaniesApi.setDefaultCompany(companyId)

      currentCompany.value = company
      saveCurrentCompanyToStorage()

      Toast.fire({
        icon: 'success',
        title: `Empresa cambiada a ${company.name}`
      });
      return true
    } catch (error) {
      toastError(error, 'Error al cambiar de empresa');
      return false
    }
  }

  const loadCurrentCompanyFromStorage = () => {
    try {
      const stored = localStorage.getItem('currentCompany')
      if (stored) {
        const company = JSON.parse(stored)
        if (companies.value.some(c => c.id === company.id)) {
          currentCompany.value = company
          return true
        }
      }
    } catch (error) {
    }
    return false
  }

  const saveCurrentCompanyToStorage = () => {
    if (currentCompany.value) {
      localStorage.setItem('currentCompany', JSON.stringify(currentCompany.value))
    } else {
      localStorage.removeItem('currentCompany')
    }
  }

  const clearCompanyContext = () => {
    companies.value = []
    currentCompany.value = null
    localStorage.removeItem('currentCompany')
  }

  const initializeCompanyContext = async () => {
    const userRole = authStore.user?.role?.name || authStore.user?.role

    if (!authStore.isAuthenticated || userRole !== 'ADMIN') {
      clearCompanyContext()
      return
    }

    try {
      await fetchUserCompanies()

      if (!hasCompanies.value) {
        return
      }

      if (!currentCompany.value) {
        const loaded = loadCurrentCompanyFromStorage()
        if (!loaded && companies.value.length > 0) {
          currentCompany.value = companies.value[0]
          saveCurrentCompanyToStorage()
        }
      }

      if (!currentCompany.value && companies.value.length > 0) {
        clearCompanyContext()
        authStore.logout()
      }
    } catch (error) {
      authStore.logout()
    }
  }

  return {
    companies,
    currentCompany,
    isLoading,

    hasCompanies,
    currentCompanyId,

    fetchUserCompanies,
    setCurrentCompany,
    loadCurrentCompanyFromStorage,
    saveCurrentCompanyToStorage,
    clearCompanyContext,
    initializeCompanyContext
  }
})
